import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { Selector } from 'shared/components'
import { sendErrorReport } from 'shared/helpers'

const AsyncSelectorEnhanced = ({
  fetchInitialOptions,
  fetchOptions,
  dataQuery,
  disabled,
  labelKey,
  multi,
  value,
  valueKey,
  isClearable,
  handleChange,
  ...rest
}) => {
  const mounted = useRef(false)
  const fetchIdRef = useRef(0)
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)

  // Load initial options
  const loadOptions = useCallback(() => {
    if (loading) return

    setLoading(true)
    fetchInitialOptions()
      .then(res => {
        if (mounted.current) {
          const fetchedOptions = get(res, dataQuery) || []
          const newOptions = fetchedOptions.map(option => ({
            label: get(option, labelKey, option.label),
            value: get(option, valueKey, option.value)
          }))
          setOptions(newOptions)

          return newOptions
        }
      })
      .catch(err => {
        if (mounted.current) {
          sendErrorReport(err, 'Async selector options could not be fetched')
        }
      })
      .finally(() => {
        if (mounted.current) {
          setLoading(false)
        }
      })
  }, [fetchInitialOptions, dataQuery, loading, labelKey, valueKey])

  // Initialize on mount
  useEffect(() => {
    if (mounted.current === false) {
      mounted.current = true
      loadOptions()
    }
    return () => {
      mounted.current = true
    }
  }, [mounted]) // Intentionally only run once on mount

  // Handle search input
  const handleSearch = query => {
    if (!query) return

    setLoading(true)
    fetchIdRef.current += 1
    const fetchId = fetchIdRef.current

    fetchOptions(query)
      .then(res => {
        if (fetchId === fetchIdRef.current && mounted.current) {
          const fetchedOptions = get(res, dataQuery) || []
          const newOptions = fetchedOptions.map(option => ({
            label: get(option, labelKey, option.label),
            value: get(option, valueKey, option.value)
          }))
          setOptions(newOptions)
        }
      })
      .catch(err => {
        if (mounted.current && fetchId === fetchIdRef.current) {
          sendErrorReport(err, 'Async selector options could not be fetched')
        }
      })
      .finally(() => {
        if (mounted.current && fetchId === fetchIdRef.current) {
          setLoading(false)
        }
      })
  }

  // Handle selection change
  const handleOptionChange = selected => {
    handleChange(selected.value)
  }

  return (
    <div className='Selector'>
      <Selector
        onChange={handleOptionChange}
        isLoading={loading}
        isDisabled={disabled}
        onInputChange={handleSearch}
        options={options}
        value={value}
        isClearable={isClearable}
        {...rest}
      />
    </div>
  )
}

AsyncSelectorEnhanced.propTypes = {
  dataQuery: PropTypes.string,
  disabled: PropTypes.bool,
  fetchOptions: PropTypes.func.isRequired,
  fetchInitialOptions: PropTypes.func.isRequired,
  handleChange: PropTypes.func,
  labelKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  valueKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  multi: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object
  ]),
  isClearable: PropTypes.bool
}

AsyncSelectorEnhanced.defaultProps = {
  dataQuery: 'data.results',
  disabled: false,
  handleChange: () => {},
  multi: false,
  value: undefined,
  isClearable: false,
  labelKey: 'label',
  valueKey: 'value'
}

export default AsyncSelectorEnhanced
